import { srmToHex } from '@beerwright/core';
import { Link } from '@tanstack/react-router';
import {
  BookOpenIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  SettingsIcon,
  ShieldIcon,
  UserRoundIcon,
  XIcon,
} from 'lucide-react';
import { useAssistant } from '@/ai/assistant-context';
import { useSession } from '@/auth/session-context';
import { AssistantOrb } from '@/components/assistant-orb';
import { CommandPaletteTrigger } from '@/components/command-palette';
import { PlanName } from '@/components/plan-icon';
import { BeerGlass } from '@/components/recipe/beer-glass';
import { SyncStatusIndicator } from '@/components/sync-status';
import { ThemeToggle } from '@/components/theme-toggle';
import { Button, buttonVariants } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { DOCS_BASE } from '@/lib/docs';
import { cmd } from '@/lib/keys';
import { NAV_ITEMS } from '@/lib/nav';
import { aiMeterLabel, planSummary } from '@/lib/plan';
import { USER_IMAGE_CLASS, userContent } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import type { Profile } from '@/profile/protocol';
import { displayNameInitials, PROFILE_SECTION_ID } from '@/profile/protocol';
import { useRecipeCount } from '@/store/workspace-context';

interface AppSidebarProps {
  readonly collapsed: boolean;
  readonly onToggleCollapsed: () => void;
  /** Overlay drawer on the phone; docked column on desktop. */
  readonly variant?: 'docked' | 'drawer';
}

/**
 * The account row's picture: the claimed profile's, or a stand-in.
 *
 * The same identity the bylines and gallery cards draw — never the OAuth
 * provider's photo, so there is one answer to "what is my picture" across the
 * app. The fallback chain is the byline's (`shared-recipe.tsx`'s `Byline`):
 * image, then initials from the display name, then the generic icon for an
 * account with no profile — which is also what a guest sees, since for them
 * the row is a sign-in link and there is nobody to draw. `alt=""` because the
 * email beside it carries the identity; announcing it twice is noise.
 *
 * Exported for its test: the sidebar itself needs a router, a workspace store
 * and the assistant context to render, and none of them have anything to say
 * about which of three circles this is.
 */
export function AccountAvatar({ profile }: { readonly profile: Profile | null }) {
  const initials = profile === null ? '' : displayNameInitials(profile.displayName);
  if (profile?.avatarUrl != null) {
    return (
      <img
        src={profile.avatarUrl}
        alt=""
        width={28}
        height={28}
        referrerPolicy="no-referrer"
        data-testid="sidebar-avatar"
        className={cn(
          'size-7 shrink-0 rounded-full border border-sidebar-border object-cover',
          // A face is the one piece of user content a text mask cannot star
          // out; the recorder replaces this with a placeholder box.
          USER_IMAGE_CLASS,
        )}
      />
    );
  }
  if (initials !== '') {
    return (
      <span
        {...userContent}
        aria-hidden="true"
        data-testid="sidebar-initials"
        className="flex size-7 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-[10px] font-medium text-sidebar-accent-foreground"
      >
        {initials}
      </span>
    );
  }
  return (
    <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-sidebar-accent-foreground">
      <UserRoundIcon className="size-3.5" aria-hidden="true" />
    </div>
  );
}

/**
 * The operator's door to the ops console (PLAN-OPS).
 *
 * Icon-only, and never a labelled row in the nav list: the console is not a
 * product surface, and a quiet shield does not announce itself on a screen
 * being demoed from the operator's own account. The label survives for
 * assistive tech and the hover.
 *
 * Expanded it sits beside the wordmark. Collapsed it moves to the utility rail
 * at the foot, beside the theme and collapse buttons, because the header row is
 * `w-16` and the glass alone is `w-10` — the two together overflowed the
 * sidebar and clipped the shield against its own border. The rail is where the
 * collapsed sidebar already keeps controls that are not navigation, so it is
 * the one place it can go without becoming a nav item.
 */
function AdminConsoleLink({ className }: { readonly className?: string }) {
  return (
    <Link
      to="/admin"
      aria-label="Admin Console"
      title="Admin Console"
      className={cn(
        'rounded-md p-1.5 text-sidebar-foreground/40 transition-colors',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        className,
      )}
      activeProps={{
        className: 'text-sidebar-accent-foreground',
        'aria-current': 'page',
      }}
    >
      <ShieldIcon className="size-4 shrink-0" aria-hidden="true" />
    </Link>
  );
}

/**
 * The gear at the foot of the sidebar: the way to Settings now that the nav
 * list has no Settings row.
 *
 * It lives in the cluster with the theme and collapse buttons in both states,
 * expanded as well as compact. It sat in the account row at first, and that is
 * exactly what was wrong with it: the row is a fixed width, so the gear took
 * its space out of the text, and the plan line — "Free · 34 of 10 recipes",
 * longest of all signed out, where the row also has to say "Sign in" — was the
 * line that truncated to pay for it. The cluster below has room the row does
 * not.
 *
 * It is its own link rather than an icon tucked inside the account row because
 * of the one case where the two disagree: signed out, the row is the sign-in
 * entry point, and a guest who only wants metric units must still be able to
 * get to the page that holds them. Keeping it separate in both states means
 * there is no sign-in branch to reason about, and the gear always means the
 * same thing.
 *
 * It points at `/settings` plain, with no section hash — the account row
 * carries the account fragment because that row is *about* the account, while
 * the gear is the whole page, units and theme included.
 *
 * Styled as a ghost icon button so it reads as a sibling of `ThemeToggle` and
 * the collapse button, which is exactly what it now is — `cursor-default`
 * included, because it takes the cluster's cursor rather than an anchor's and
 * the three controls should feel like one row under the mouse.
 */
function SettingsGearLink({ className }: { readonly className?: string }) {
  return (
    <Link
      to="/settings"
      aria-label="Settings"
      title="Settings"
      className={cn(
        buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
        'cursor-default',
        className,
      )}
      // No `activeProps`: the theme toggle and the collapse button beside it
      // never light up, and a gear that did read as a pressed button rather
      // than a way somewhere. The settings page marks itself.
    >
      <SettingsIcon aria-hidden="true" />
    </Link>
  );
}

/**
 * The way to the documentation from anywhere in the app.
 *
 * Every other docs link in the app hangs off the surface it documents — a
 * question mark beside the fermentables table opens the fermentables page
 * (`DocsLink`) — which is exactly right when you already know which thing you
 * are confused about, and no help at all when you do not. This one
 * opens the front door instead, and it is in the footer cluster because that
 * is where the app's own controls live rather than any one page's.
 *
 * A plain `<a>` rather than a router `Link`: the docs are a different site, so
 * there is no route to give the router, and `target="_blank"` keeps the
 * workspace where it was — the app's idiom for an outbound link, the same one
 * `DocsLink` follows.
 *
 * It takes the cluster's `cursor-default` the way the gear does, and leaves
 * the icon's colour and size alone, so the book, the gear and the moon read as
 * one row of three rather than three borrowed buttons.
 */
function DocsIconLink({ className }: { readonly className?: string }) {
  return (
    <a
      href={DOCS_BASE}
      target="_blank"
      rel="noreferrer"
      aria-label="Docs"
      title="Docs"
      className={cn(
        buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
        'cursor-default',
        className,
      )}
    >
      <BookOpenIcon aria-hidden="true" />
    </a>
  );
}

export function AppSidebar({ collapsed, onToggleCollapsed, variant = 'docked' }: AppSidebarProps) {
  const recipesUsed = useRecipeCount();
  const session = useSession();
  const assistant = useAssistant();
  const signedIn = session.user !== null;
  // The count is the workspace's own recipe table; the plan comes from D1
  // through `/api/me`. A capped plan reads "Free · 7 of 10 recipes"; an
  // uncapped one is just its name, because there is no number worth watching.
  const plan = planSummary(session.entitlement, recipesUsed);
  // The one exception: a plan whose assistant allowance is something it sells —
  // Beerwright today — gets a quiet line once more than half of it is gone
  // (PLAN §9). Free and Brewmaster share the same taste, so a meter there would
  // be a countdown rather than information.
  const aiMeter = aiMeterLabel(assistant.allowance);
  const drawer = variant === 'drawer';
  const compact = collapsed && !drawer;
  // The ops console (PLAN-OPS). Shown to the person it belongs to rather than
  // hidden behind a URL only they know — the Worker is what refuses everyone
  // else, and it refuses them whether or not this link is on screen.
  const isAdmin = session.user?.role === 'admin';

  return (
    <aside
      data-collapsed={compact}
      className={cn(
        'flex h-full min-h-0 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
        'transition-[width] duration-200 ease-out',
        drawer ? 'w-[min(18.75rem,85vw)]' : compact ? 'w-16' : 'w-60',
        !drawer && 'hidden md:flex',
      )}
    >
      <div
        className={cn('flex h-14 items-center gap-2 px-3', compact && 'justify-center gap-1 px-0')}
      >
        <BeerGlass color={srmToHex(8)} logo className="h-8 w-10 shrink-0" />
        {!compact && (
          <span data-slot="wordmark" className="truncate">
            Beerwright
          </span>
        )}
        {isAdmin && !compact && <AdminConsoleLink className="ml-auto" />}
      </div>

      <Separator className="bg-sidebar-border" />

      {/* The two ways into something, above the list of places to go: the
          assistant, and the palette. The palette's button is here rather than
          in a toolbar because this column is on screen from `md` up, which is
          every width a keyboard is not guaranteed at. */}
      <div className={cn('flex flex-col gap-1 p-2', compact && 'items-center')}>
        <Button
          type="button"
          variant="ghost"
          size={compact ? 'icon' : 'sm'}
          aria-label={assistant.open ? 'Close the assistant' : 'Open the assistant'}
          aria-pressed={assistant.open}
          title={compact ? `Assistant (${cmd('J')})` : undefined}
          onClick={() => assistant.toggle()}
          className={cn(
            compact ? undefined : 'h-8 w-full justify-start gap-2 px-2',
            assistant.open && 'bg-sidebar-accent text-sidebar-accent-foreground',
          )}
        >
          <AssistantOrb />
          {!compact && (
            <>
              <span>Assistant</span>
              {/* No legend in the drawer: that variant only ever appears below
                  `md`, behind the phone's hamburger, and a phone has neither ⌘
                  nor Ctrl to press. The shortcut still works for anyone who
                  brings a keyboard — this is the label going, not the key. */}
              {!drawer && (
                <kbd className="ml-auto rounded border border-sidebar-border bg-sidebar-accent px-1 font-mono text-[10px] text-muted-foreground">
                  {cmd('J')}
                </kbd>
              )}
            </>
          )}
        </Button>

        {/* Not in the drawer: the phone's top bar carries this now, where a
            thumb reaches it without opening a menu first. A tablet with a
            docked sidebar and no keyboard still gets it here, which is the
            audience `CommandPaletteTrigger` renders for. */}
        {!drawer && <CommandPaletteTrigger compact={compact} />}
      </div>

      <Separator className="bg-sidebar-border" />

      <nav aria-label="Main" className="flex-1 overflow-y-auto p-2">
        <ul className="flex flex-col gap-0.5">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <Link
                to={to}
                // Collapsed, the label is gone from the DOM and the icon is
                // `aria-hidden`, which left the link with no accessible name at
                // all. A `title` alone does not fix that: it is a hover, and
                // the hand holding a tablet has nothing to hover with.
                title={compact ? label : undefined}
                aria-label={compact ? label : undefined}
                className={cn(
                  'flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium',
                  'text-sidebar-foreground/70 transition-colors',
                  'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                  compact && 'justify-center px-0',
                )}
                // No `activeOptions`: `exact` defaults to false, so a section
                // stays lit while you are inside it — Profiles on
                // `/profiles/mash`, Batches on a batch. Worth knowing before
                // reaching for a flag, as I did.
                activeProps={{
                  className: 'bg-sidebar-accent text-sidebar-accent-foreground',
                  'aria-current': 'page',
                }}
              >
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                {!compact && <span className="truncate">{label}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <Separator className="bg-sidebar-border" />

      {/* `data-slot` so the documentation rig can photograph the account row,
          the plan and the sync indicator as one thing (PLAN-DOCS A1) — the
          three answers to "whose workspace is this and is it safe".

          The bottom padding grows by the safe-area inset below `md`, exactly
          as `app-main` in `app-shell.tsx` does: the drawer runs edge to edge
          under Android's gesture bar, which Chrome draws over the page when
          `viewport-fit=cover` is set, and without the inset the gear and the
          close button sit beneath it. Desktop has no such bar and keeps the
          plain padding. */}
      <div
        data-slot="sidebar-footer"
        className={cn(
          'flex flex-col gap-2 p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] md:pb-2',
          compact && 'items-center',
        )}
      >
        {/* The account row goes where the account goes: signed out it is the
            sign-in entry point (where a guest who wants their recipes on a
            second device is trying to get to), and signed in it lands on
            Settings → Account — the one section that owns everything this row
            shows: the address, the plan, and the display name and picture it
            draws. The fragment rides the settings page's own anchor scrolling
            (`useSectionAnchor`).

            Settings used to be a labelled row in the nav list as well, which
            made two doors to one page; that row went because it duplicated this
            one. The visible way to Settings is the gear in the cluster below,
            not an icon in here: the row's width is spoken for by the address
            and the plan line, and a gear on the end of it only ever comes out
            of that text. The row is the account. */}
        <Link
          to={signedIn ? '/settings' : '/signin'}
          {...(signedIn ? { hash: PROFILE_SECTION_ID, hashScrollIntoView: true } : {})}
          className={cn(
            'flex items-center gap-2 rounded-md px-1.5 py-1.5',
            'transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
            compact && 'justify-center px-0',
          )}
          title={compact ? `${signedIn ? session.user?.email : 'Sign in'} · ${plan}` : undefined}
        >
          <AccountAvatar profile={session.profile} />
          <div className={cn('min-w-0 leading-tight', compact && 'sr-only')}>
            <p className="truncate text-xs font-medium" data-testid="account-label">
              {/* The address is the account's; the word "Sign in" is ours. */}
              {signedIn ? <span {...userContent}>{session.user?.email}</span> : 'Sign in'}
            </p>
            <p className="truncate text-xs text-muted-foreground" data-testid="plan-summary">
              <PlanName plan={session.entitlement.plan}>{plan}</PlanName>
            </p>
            {aiMeter !== null && (
              <p className="truncate text-xs text-muted-foreground" data-testid="ai-meter">
                {aiMeter}
              </p>
            )}
          </div>
        </Link>

        <SyncStatusIndicator collapsed={compact} />

        {/* The app's own controls, as opposed to the page's: expanded they are
            one row — gear, theme, docs on the left, the collapse button off on
            the right.

            Compact the column is `w-16`, a single file of icons, and what a
            narrow rail should hold is what a brewer reaches for mid-brew. The
            theme is not that: it changes nothing about the beer, and Settings
            carries it too, as a select. Nor are the docs, which are a sit-down
            read on another site. Both come back the moment the column is
            widened, so nothing is lost — only moved one click away while the
            space is scarce. */}
        <div className={cn('flex items-center gap-1', compact ? 'flex-col' : 'justify-between')}>
          {compact ? (
            <>
              {isAdmin && <AdminConsoleLink />}
              <SettingsGearLink className="size-9" />
            </>
          ) : (
            <div className="flex items-center gap-1">
              <SettingsGearLink className="size-9" />
              <ThemeToggle />
              <DocsIconLink className="size-9" />
            </div>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onToggleCollapsed}
            aria-label={drawer ? 'Close menu' : compact ? 'Expand sidebar' : 'Collapse sidebar'}
            title={drawer ? 'Close menu' : compact ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {drawer ? <XIcon /> : compact ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
          </Button>
        </div>
      </div>
    </aside>
  );
}
